import { zodResolver } from '@hookform/resolvers/zod';
import { Group, Stack } from '@mantine/core';
import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EditorialTeamMembershipSelectControl } from '@/module/Nomen/EditorialTeamMembership/EditorialTeamMembershipSelectControl';

import {
  AuthorListControl,
  Content,
  EnumSelectControl,
  Form,
  FormSubmit,
  NumberInputControl,
  TextInputControl,
} from '@/core/components';

import { EditorialTeamSchema } from './model/EditorialTeamSchema';
import { journalReputationTypeList, publisherReputationTypeList } from './model/EditorialTeamTypes';

interface EditorialTeamFormProps extends FormSubmit<EditorialTeamSchema> {
  title: string;
}

export function EditorialTeamForm(props: EditorialTeamFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit } = props;
  const { t } = useTranslation();
  const form = useForm<EditorialTeamSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  const selectedType = useWatch({
    control: form.control,
    name: 'type',
  });

  useEffect(() => {
    if (selectedType === 'JOURNAL') {
      form.setValue('publisherName', null);
      form.setValue('publisherReputation', null);
    } else if (selectedType === 'PUBLISHER') {
      form.setValue('journalName', null);
      form.setValue('journalReputation', null);
    }
  }, [selectedType, form]);

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            <Group align="flex-end">
              <EnumSelectControl<EditorialTeamSchema>
                name="type"
                label={t('editorialTeams.form.fields.type')}
                isSubmitting={isSubmitting}
                options={[
                  { value: 'JOURNAL', label: t('editorialTeams.typeEnum.JOURNAL') },
                  { value: 'PUBLISHER', label: t('editorialTeams.typeEnum.PUBLISHER') },
                ]}
              />
              <EnumSelectControl<EditorialTeamSchema>
                name="reachType"
                label={t('editorialTeams.form.fields.reachType')}
                isSubmitting={isSubmitting}
                options={[
                  { value: 'NATIONAL', label: t('editorialTeams.influenceEnum.NATIONAL') },
                  { value: 'INTERNATIONAL', label: t('editorialTeams.influenceEnum.INTERNATIONAL') },
                ]}
              />
            </Group>

            {selectedType === 'JOURNAL' && (
              <Group align="flex-end">
                <TextInputControl<EditorialTeamSchema>
                  name="journalName"
                  label={t('editorialTeams.form.fields.journalName')}
                  isSubmitting={isSubmitting}
                />
                <EnumSelectControl<EditorialTeamSchema>
                  name="journalReputation"
                  label={t('editorialTeams.form.fields.journalReputation')}
                  isSubmitting={isSubmitting}
                  options={journalReputationTypeList.map(reputation => ({
                    value: reputation,
                    label: t(`editorialTeams.journalReputationEnum.${reputation}`),
                  }))}
                />
              </Group>
            )}

            {selectedType === 'PUBLISHER' && (
              <Group align="flex-end">
                <TextInputControl<EditorialTeamSchema>
                  name="publisherName"
                  label={t('editorialTeams.form.fields.publisherName')}
                  isSubmitting={isSubmitting}
                />
                <EnumSelectControl<EditorialTeamSchema>
                  name="publisherReputation"
                  label={t('editorialTeams.form.fields.publisherReputation')}
                  isSubmitting={isSubmitting}
                  options={publisherReputationTypeList.map(reputation => ({
                    value: reputation,
                    label: t(`editorialTeams.publisherReputationEnum.${reputation}`),
                  }))}
                />
              </Group>
            )}

            <Group align="flex-end">
              <NumberInputControl<EditorialTeamSchema>
                name="startYear"
                label={t('editorialTeams.form.fields.startYear')}
                isSubmitting={isSubmitting}
                allowDecimal={false}
                flex={0.5}
              />
              <NumberInputControl<EditorialTeamSchema>
                name="endYear"
                label={t('editorialTeams.form.fields.endYear')}
                isSubmitting={isSubmitting}
                allowDecimal={false}
                flex={0.5}
              />
            </Group>
          </Content>
          <Content type="full">
            <AuthorListControl<EditorialTeamSchema>
              name="authors"
              isSubmitting={isSubmitting}
              label={t('authors.form.title')}
              renderMembership={({ membershipName }) => (
                <EditorialTeamMembershipSelectControl
                  label={t('authors.form.fields.membership')}
                  name={membershipName}
                  isSubmitting={isSubmitting}
                  isDisabled={isSubmitting}
                />
              )}
            />
          </Content>
        </Stack>
      </Form>
    </FormProvider>
  );
}
